import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  inviteDriver,
  fetchBaseLocations,
} from "../features/driver/driverSlice";
import { contactPersonIcon, contactNumberIcon, emailIcon } from "../assets";
import CustomAlert from "../components/CustomAlert";
import "../css/InviteDriver.css";

const InviteDriver = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, error, baseLocations, baseLocationsLoading } = useSelector(
    (state) => state.driver,
  );

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [baseDropdownOpen, setBaseDropdownOpen] = useState(false);

  // Custom Alert State
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    type: "success",
    title: "",
    message: "",
  });

  // Custom Alert Helper
  const showCustomAlert = (type, title, message) => {
    setAlertConfig({ type, title, message });
    setShowAlert(true);
  };

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobileNo: "",
    email: "",
    baseLocationIds: [],
  });

  useEffect(() => {
    dispatch(fetchBaseLocations());
  }, [dispatch]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    if (!formData.mobileNo.trim()) {
      errors.mobileNo = "Mobile number is required";
    } else if (!/^[0-9]{10}$/.test(formData.mobileNo)) {
      errors.mobileNo = "Mobile number must be 10 digits";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Invalid email format";
    }

    if (formData.baseLocationIds.length === 0) {
      errors.baseLocationIds = "Please select at least one base location";
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      // Prepare data for API (matching backend field names)
      const inviteData = {
        FirstName: formData.firstName,
        LastName: formData.lastName,
        MobileNumber: formData.mobileNo,
        Email: formData.email,
        BaseLocationIds: formData.baseLocationIds,
      };

      // Call API
      await dispatch(inviteDriver(inviteData)).unwrap();

      // Show success modal
      setShowSuccessModal(true);

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        mobileNo: "",
        email: "",
        baseLocationIds: [],
      });
    } catch (error) {
      showCustomAlert(
        "error",
        "Invite Failed",
        error || "Failed to invite driver. Please try again.",
      );
    }
  };

  const handleBaseToggle = (baseId) => {
    setFormData((prev) => ({
      ...prev,
      baseLocationIds: prev.baseLocationIds.includes(baseId)
        ? prev.baseLocationIds.filter((id) => id !== baseId)
        : [...prev.baseLocationIds, baseId],
    }));
    if (formErrors.baseLocationIds) {
      setFormErrors((prev) => ({ ...prev, baseLocationIds: "" }));
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowSuccessModal(false);
    navigate("/driver", { replace: true });
  };

  return (
    <div className="invite-driver-page">
      <div className="invite-container">
        <div className="invite-card">
          <div className="invite-header">
            <h1 className="invite-title">Invite Driver</h1>
            <p className="invite-description">
              Invite drivers or team members to your portal. They'll get a link
              to register and upload documents themselves
            </p>
          </div>

          <form onSubmit={handleSubmit} className="invite-form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">First Name</label>
                <div
                  className={`input-wrapper ${formErrors.firstName ? "error" : ""}`}
                >
                  <img src={contactPersonIcon} alt="" className="input-icon" />
                  <input
                    type="text"
                    name="firstName"
                    className="form-control"
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </div>
                {formErrors.firstName && (
                  <span className="error-message">{formErrors.firstName}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Last Name</label>
                <div
                  className={`input-wrapper ${formErrors.lastName ? "error" : ""}`}
                >
                  <img src={contactPersonIcon} alt="" className="input-icon" />
                  <input
                    type="text"
                    name="lastName"
                    className="form-control"
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>
                {formErrors.lastName && (
                  <span className="error-message">{formErrors.lastName}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Mobile No</label>
                <div
                  className={`input-wrapper ${formErrors.mobileNo ? "error" : ""}`}
                >
                  <img src={contactNumberIcon} alt="" className="input-icon" />
                  <input
                    type="tel"
                    name="mobileNo"
                    className="form-control"
                    placeholder="Mobile No"
                    value={formData.mobileNo}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10);
                      setFormData((prev) => ({ ...prev, mobileNo: value }));
                    }}
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    maxLength={10}
                  />
                </div>
                {formErrors.mobileNo && (
                  <span className="error-message">{formErrors.mobileNo}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Email-id</label>
                <div
                  className={`input-wrapper ${formErrors.email ? "error" : ""}`}
                >
                  <img src={emailIcon} alt="" className="input-icon" />
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    placeholder="Email-id"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                {formErrors.email && (
                  <span className="error-message">{formErrors.email}</span>
                )}
              </div>
            </div>
            <div className="form-group full-width">
              <label className="form-label">Choose Base</label>
              <div className="base-dropdown-wrapper">
                {/* Trigger */}
                <div
                  className={`base-dropdown-trigger ${baseDropdownOpen ? "open" : ""} ${formErrors.baseLocationIds ? "error" : ""}`}
                  onClick={() => setBaseDropdownOpen((prev) => !prev)}
                >
                  <svg
                    className="base-trigger-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span className={`base-trigger-text ${formData.baseLocationIds.length > 0 ? 'selected' : ''}`}>
  {formData.baseLocationIds.length > 0
    ? baseLocations
        .filter(base => formData.baseLocationIds.includes(base.BaseId))
        .map(base => `${base.BaseName}, ${base.City}`)
        .join(' | ')
    : 'Choose Base'}
</span>
                  <svg
                    className={`base-chevron ${baseDropdownOpen ? "rotated" : ""}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>

                {/* Dropdown List */}
                {baseDropdownOpen && (
                  <div className="base-dropdown-list">
                    {baseLocationsLoading ? (
                      <p className="loading-text">Loading locations...</p>
                    ) : baseLocations.length === 0 ? (
                      <p className="loading-text">No base locations found</p>
                    ) : (
                      baseLocations.map((base) => (
                        <label key={base.BaseId} className="base-checkbox-item">
                          <input
                            type="checkbox"
                            checked={formData.baseLocationIds.includes(
                              base.BaseId,
                            )}
                            onChange={() => handleBaseToggle(base.BaseId)}
                          />
                          <span className="base-info">
                            <span className="base-name">
                              {base.BaseName}, {base.City}, {base.State}
                            </span>
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>
              {formErrors.baseLocationIds && (
                <span className="error-message">
                  {formErrors.baseLocationIds}
                </span>
              )}
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={isLoading}
            >
              {isLoading ? "SENDING..." : "SEND INVITE"}
            </button>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal-overlay">
          <div className="success-modal">
            <button className="close-modal-btn" onClick={handleCloseModal}>
              ✖
            </button>

            <div className="success-icon">
              <div className="check-circle">
                <span className="check-mark">✓</span>
              </div>
            </div>

            <h2 className="success-title">Invite Sent Successfully</h2>
            <p className="success-message">
              Click close button to return to drivers page
            </p>
          </div>
        </div>
      )}

      {/* Custom Alert */}
      <CustomAlert
        show={showAlert}
        onClose={() => setShowAlert(false)}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
      />
    </div>
  );
};

export default InviteDriver;
